import React from "react";
import {
	Theme,
	Grid,
	Typography,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableSortLabel,
	TableBody,
	makeStyles,
} from "@material-ui/core";
import classNames from "classnames";

import { PremiumSermon } from "../../../entities/PremiumSermon";
import { PremiumSermonSeries } from "../../../entities/PremiumSermonSeries";
import { LibraryItemType, printLibraryItemType } from "../../../entities/LibraryItemType";
import { printAssetCategory } from "../../../entities/AssetCategory";
import { MarketingPageType } from "../types/MarketingPageType";
import { Hero } from "./hero";
import { orderBy } from "../../../utilities/orderBy";

const useStyles = makeStyles((theme: Theme) => ({
	autoMargin: {
		marginLeft: "auto",
		marginRight: "auto",
	},
	media: {
		maxWidth: "100%",
		maxHeight: "100%",
		borderRadius: "4px",
	},
	mediaContainer: {
		marginTop: "15px",
		marginBottom: "5px",
		paddingTop: "15px",
		minWidth: "fit-content",
		[theme.breakpoints.down("xs")]: {
			marginRight: "10px",
			marginLeft: "10px",
		},
	},
	longDescriptionContainer: {
		marginTop: "5px",
		marginBottom: "10px",
		padding: "0 15px",
		maxWidth: "initial",
	},
	longDescriptionText: {
		fontSize: "1.1rem",
		maxWidth: "600px",
		display: "block",
		margin: "0 auto",
		lineHeight: "1.85rem",
		width: "100%",
	},
	tableContainer: {
		maxWidth: "649px",
		width: "100%",
		display: "block",
		margin: "15px auto 30px",
	},
}));

interface Props{
	asset: PremiumSermon | PremiumSermonSeries;
	callToActionUrl: string;
	pageType: MarketingPageType;
}

export function ProductPageContent({ callToActionUrl, pageType, asset }: Props) {
	const classes = useStyles();
	const headerPreText = `POPULAR ${pageType === MarketingPageType.Series ? "SERMON SERIES" : "SERMONS"}`;
	const buttonText = "UNLOCK FREE TODAY";
	const buttonSubText = `Unlock all premium ${pageType === MarketingPageType.Series ? "sermon series" : "sermons"} FREE with Sermon Maker`;

	const renderSeriesAssets = (series: PremiumSermonSeries) => {
		let assets = series.assets.sort(orderBy.string((asset) => asset.name, "Ascending"));

		return assets.map(
			(asset) =>
				asset.type !== LibraryItemType.Folder && (
					<TableRow key={asset.id}>
						<TableCell>{asset.name}</TableCell>
						<TableCell>{asset.category ? printAssetCategory(asset.category) : printLibraryItemType(asset.type)}</TableCell>
					</TableRow>
				)
		);
	};

	return (
		<>
			<Hero
				assetName={asset.name}
				assetShortDescription={asset.shortDescription}
				headerPreText={headerPreText}
				buttonText={buttonText}
				buttonSubText={buttonSubText}
				callToActionUrl={callToActionUrl}
			/>
			<Grid container direction="column">
				<Grid item xs={6} className={classNames(classes.autoMargin, classes.mediaContainer)}>
					<img alt="sermon header" src={asset.thumbnailUrl} className={classes.media} />
				</Grid>
				<Grid item xs={4} className={classNames(classes.autoMargin, classes.longDescriptionContainer)}>
					<Typography className={classes.longDescriptionText}>{asset.description}</Typography>
				</Grid>
				{pageType === MarketingPageType.Series && (
					<Grid container item xs={6} className={classNames(classes.autoMargin, classes.tableContainer)}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>
										<TableSortLabel>Title</TableSortLabel>
									</TableCell>
									<TableCell>
										<TableSortLabel>File</TableSortLabel>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>{renderSeriesAssets(asset as PremiumSermonSeries)}</TableBody>
						</Table>
					</Grid>
				)}
			</Grid>
		</>
	);
}